<template>
    <div>
        <div class="row gutter-xs">
            <div class="panel panel-default">
                <div class="panel-body">
                    <div class="flex flex-col items-center justify-between md:flex-row">
                        <div class="hidden lg:block lg:w-1/3"></div>
                        <div class="flex justify-center lg:justify-center md:justify-end md:w-1/3 w-full">
                            <div class="form-group form-group-sm">
                                <div class="input-with-icon mx-auto w-300px">
                                    <input class="form-control input-sm" type="search" v-model="searchQuery" :placeholder="$t('invoices.kiosk_search_placeholder')">
                                    <span class="icon icon-search input-icon"></span>
                                </div>
                            </div>
                        </div>
                        <template v-if="userIsDeveloper">
                            <div class="mb-6 md:mb-0 md:mt-0 mt-2 md:w-1/3 self-start w-full">
                                <div class="flex justify-center md:justify-end">
                                    <a href="/kiosk/invoices/create" class="btn btn-primary btn-sm btn-labeled">
                                        <span class="btn-label">
                                            <span class="icon icon-plus icon-lg icon-fw"></span>
                                        </span>
                                        {{ $t('invoices.new_invoice') }}
                                    </a>
                                </div>
                            </div>
                        </template>
                        <template v-else>
                            <div class="hidden lg:block lg:w-1/3"></div>
                        </template>
                    </div>
                    <vue-good-table
                        mode="remote"
                        @on-page-change="onPageChange"
                        @on-sort-change="onSortChange"
                        @on-per-page-change="onPerPageChange"
                        @on-search="onSearchChange"
                        :search-options="{ enabled: true, externalQuery: serverParams.search }"
                        :sort-options="{
                            enabled: true,
                            initialSortBy: { field: 'created_at', type: 'desc' },
                        }"
                        :pagination-options="{
                            enabled: true,
                            perPage: 20,
                            setCurrentPage: table.currentPage,
                            nextLabel: $t('tables.next'),
                            prevLabel: $t('tables.previous'),
                            rowsPerPageLabel: $t('tables.rows_per_page'),
                            ofLabel: $t('tables.of'),
                            pageLabel: $t('tables.page'),
                            allLabel: $t('tables.all'),
                        }"
                        :columns="table.columns"
                        :rows="table.rows"
                        :total-rows="table.totalRecords"
                        :is-loading="table.isLoading"
                        style-class="table vgt-table striped">
                        <div slot="emptystate">
                            <div class="row text-center p-y-md">
                                <div class="col-xs-8 col-xs-offset-2">
                                    <p>{{ $t('tables.no_records_found') }}</p>
                                </div>
                            </div>
                        </div>
                        <template slot="table-row" slot-scope="props">
                            <template v-if="props.column.field === 'invoice'">
                                <div class="flex items-center">
                                    {{ props.row.identificator }}
                                </div>
                            </template>
                            <template v-if="props.column.field === 'organization'">
                                <div class="flex items-center">
                                    <div class="mr-3">
                                        <organization-logo :organization="props.row.organization" :size="'32px'"></organization-logo>
                                    </div>
                                    <div class="flex flex-col">
                                        <div class="font-bold" v-html="props.row.organization.name"></div>
                                        <div class="font-medium text-gray-table-light text-xs">
                                            ({{ $tc('organizations.users_count', props.row.organization.users_count, { value: props.row.organization.users_count }) }})
                                        </div>
                                    </div>
                                </div>
                            </template>
                            <template v-else-if="props.column.field === 'created_at'">
                                <div class="flex items-center">
                                    {{ formatDate(props.row.created_at) }}
                                </div>
                            </template>
                            <template v-else-if="props.column.field === 'status'">
                                <div class="inline-flex items-center">
                                    <template v-if="props.row.status === 'pending'">
                                        <div style="padding: 0 1px;">
                                            <span class="icon icon-refresh icon-lg icon-spin mr-2 text-warning-500"></span>
                                        </div>
                                    </template>
                                    <template v-else-if="props.row.status === 'paid'">
                                        <div style="padding: 0px;">
                                            <span class="icon icon-check icon-lg mr-2 text-primary-500"></span>
                                        </div>
                                    </template>
                                    <template v-else-if="['unpaid', 'payment_failed', 'canceled'].includes(props.row.status)">
                                        <div style="padding: 0 2px;">
                                            <span class="icon icon-times icon-lg mr-2 text-danger-500"></span>
                                        </div>
                                    </template>
                                    <template v-if="props.row.status === 'pending'">
                                        <span class="text-warning-600">{{ invoiceStatuses[props.row.status] }}</span>
                                    </template>
                                    <template v-else-if="props.row.status === 'paid'">
                                        <span class="text-primary-600">{{ invoiceStatuses[props.row.status] }}</span>
                                    </template>
                                    <template v-else-if="['unpaid', 'payment_failed', 'canceled'].includes(props.row.status)">
                                        <span class="text-danger-600">{{ invoiceStatuses[props.row.status] }}</span>
                                    </template>
                                </div>
                                <div v-if="props.row.status === 'paid'" class="font-medium text-gray-table-light text-xs">
                                    <template v-if="paymentProcessors[props.row.payment_processor]">
                                        ({{ paymentProcessors[props.row.payment_processor] }})
                                    </template>
                                    <template v-else>
                                        ({{ $t('globals.n_a') }})
                                    </template>
                                </div>
                            </template>
                            <template v-else-if="props.column.field === 'total'">
                                &euro;{{ props.row.total.toFixed(2) }}
                            </template>
                            <template v-else-if="props.column.field === 'actions'">
                                <div class="flex items-center justify-center space-x-2">
                                    <template v-if="(userIsDeveloper || userIsFinancial) && ['unpaid', 'payment_failed'].includes(props.row.status)">
                                        <button @click="markInvoiceAsPaid(props.row)" class="btn btn-default btn-xs" v-tooltip="{ content: $t('organization_payments.mark_as_paid'), placement: 'top' }">
                                            <span class="icon icon-check"></span>
                                        </button>
                                    </template>
                                    <a :href="`/organization/payments/invoice/${props.row.id}/download`" class="btn btn-outline-primary btn-xs" v-tooltip="{ content: $t('globals.download'), placement: 'top' }">
                                        <span class="icon icon-download"></span>
                                    </a>
                                    <a :href="`/organization/payments/invoice/${props.row.id}/download-xml-einvoice`" class="btn btn-outline-primary btn-xs" v-tooltip="{ content: $t('invoices.download_xml_einvoice'), placement: 'top' }">
                                        <span class="icon icon-file-code-o"></span>
                                    </a>
                                </div>
                            </template>
                            <span v-else v-html="props.formattedRow[props.column.field]"></span>
                        </template>
                    </vue-good-table>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { lightFormat, parseISO } from 'date-fns';
import OrganizationLogo from '../../../user/partials/organization-logo.vue';

export default {
    components: {
        'organization-logo': OrganizationLogo,
    },

    props: {
        initialSort: {
            type: Array,
            default: function() {
                return [
                    { column: 'created_at', dir: 'desc' },
                ];
            },
        },
        invoiceStatuses: {
            type: Object,
            required: true,
        },
        paymentProcessors: {
            type: Object,
            required: true,
        },
        userIsDeveloper: {
            type: Boolean,
            required: true,
        },
        userIsFinancial: {
            type: Boolean,
            required: true,
        },
    },

    data() {
        return {
            table: {
                columns: [
                    {
                        label: this.$t('globals.invoice'),
                        field: 'invoice',
                        sortable: false,
                    },
                    {
                        label: this.$t('globals.organization'),
                        field: 'organization',
                    },
                    {
                        label: this.$t('globals.date'),
                        field: 'created_at',
                    },
                    {
                        label: this.$t('organization_payments.paid'),
                        field: 'status',
                    },
                    {
                        label: this.$t('organization_payments.total'),
                        field: 'total',
                        firstSortType: 'desc',
                    },
                    {
                        label: this.$t('globals.actions'),
                        field: 'actions',
                        html: true,
                        sortable: false,
                        thClass: 'text-center',
                    },
                ],
                rows: [],
                totalRecords: 0,
                currentPage: 1,
                isLoading: false,
            },
            serverParams: {
                order: [],
                start: 0,
                length: 20,
                search: '',
            },
            searchQuery: '',
        };
    },

    watch: {
        searchQuery: Globals.debounce(function (value) {
            this.serverParams.search = value;
        }, 750)
    },

    methods: {
        markInvoiceAsPaid(invoice) {
            Bus.$emit('showMarkInvoiceAsPaidModal', { invoice: invoice });
        },

        updateParams(newProps) {
            this.serverParams = Object.assign({}, this.serverParams, newProps);
        },

        onPageChange(params) {
            this.table.currentPage = params.currentPage;
            let start = 0;
            if (params.currentPage > 1) {
                start = (params.currentPage - 1) * params.currentPerPage;
            }
            this.updateParams({ start: start });
            this.loadItems();
        },

        onPerPageChange(params) {
            this.updateParams({ length: params.currentPerPage });
            this.loadItems();
        },

        onSortChange(params) {
            const newOrder = [];
            newOrder.push({ 'column': _.head(params)['field'], 'dir': _.head(params)['type'] });
            this.updateParams({ order: newOrder });
            this.loadItems();
        },

        onSearchChange(params) {
            this.updateParams({ search: params.searchTerm });
            this.loadItems();
        },

        goToFirstPage() {
            this.table.currentPage = 1;
            this.updateParams({ start: 0 });
            this.loadItems();
        },

        loadItems() {
            this.table.isLoading = true;

            const queryParams = Object.assign({}, this.serverParams);

            axios.get('/data/kiosk/invoices', { params: queryParams })
                .then(response => {
                    this.table.totalRecords = response.data.recordsFiltered;
                    this.table.rows = response.data.data;
                    this.table.isLoading = false;
                });
        },

        formatDate(date) {
            return lightFormat(parseISO(date), 'dd-MM-y');
        },
    },
};
</script>
